// Shared spreadsheet (xlsx/csv) helpers behind the นำเข้า / ส่งออก buttons on
// the accounting pages. Extracted from the proven /coa implementation
// (routes/coa.tsx) so every module's import/export behaves identically.
//
// SheetJS (~165 kB gzip) is imported on demand inside each function so it never
// weighs down a page that only displays data — the chunk loads the first time a
// user actually exports or imports.

export type CellValue = string | number | boolean;

// Local YYYY-MM-DD for export filenames, e.g. ผังบัญชี_DCC_2026-05-29.xlsx
export function todayStamp(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export interface DownloadXlsxOptions {
  // Worksheet (tab) name — keep it short; Thai is fine. Defaults to 'Sheet1'.
  sheetName?: string;
  // Column widths (in chars). One entry per column, in order.
  colWidths?: { wch: number }[];
}

// Build a single-sheet workbook from a header+rows grid and trigger a .xlsx
// download in the browser. `aoa[0]` is treated as the header row.
export async function downloadXlsx(
  filename: string,
  aoa: CellValue[][],
  opts: DownloadXlsxOptions = {},
): Promise<void> {
  const XLSX = await import('xlsx');
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  if (opts.colWidths) ws['!cols'] = opts.colWidths;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, opts.sheetName ?? 'Sheet1');
  XLSX.writeFile(wb, filename, { bookType: 'xlsx', compression: true });
}

// Build a multi-sheet workbook and trigger a .xlsx download. `sheets` is an
// ordered array of { name, aoa } — `name` is sanitized to the 31-char Excel
// sheet-name limit (strips invalid chars, truncates). `aoa[0]` is the header.
export async function downloadWorkbook(
  filename: string,
  sheets: { name: string; aoa: CellValue[][] }[],
): Promise<void> {
  const XLSX = await import('xlsx');
  const wb = XLSX.utils.book_new();
  for (const sheet of sheets) {
    const safeName = sheet.name
      .replace(/[[\]\\/:?*]/g, '_') // strip chars forbidden in Excel sheet names
      .slice(0, 31);
    const ws = XLSX.utils.aoa_to_sheet(sheet.aoa);
    XLSX.utils.book_append_sheet(wb, ws, safeName || 'Sheet');
  }
  XLSX.writeFile(wb, filename, { bookType: 'xlsx', compression: true });
}

// A column spec for the generic exporter. `header` is the stable key written to
// the first row — keep it the SAME key the matching importer reads so an
// exported file round-trips straight back through นำเข้า. `value` pulls a cell
// out of a row.
export interface ExportColumn<T> {
  header: string;
  value: (row: T) => CellValue;
  width?: number;
}

// Export a list of rows to .xlsx using the column specs. The header row is the
// column `header` keys; subsequent rows are `value(row)` per column.
export async function exportRows<T>(
  filename: string,
  sheetName: string,
  columns: ExportColumn<T>[],
  rows: readonly T[],
): Promise<void> {
  const header: CellValue[] = columns.map((c) => c.header);
  const body: CellValue[][] = rows.map((r) => columns.map((c) => c.value(r)));
  await downloadXlsx(filename, [header, ...body], {
    sheetName,
    colWidths: columns.map((c) => ({ wch: c.width ?? 16 })),
  });
}

// Cell -> string. With raw:false SheetJS yields formatted primitives; guard the
// unknown so eslint's no-base-to-string stays happy and objects never leak in.
export function cellToString(c: unknown): string {
  if (c === null || c === undefined) return '';
  if (typeof c === 'string') return c;
  if (typeof c === 'number' || typeof c === 'boolean') return String(c);
  return '';
}

// Read an uploaded spreadsheet (.xlsx / .xls / .csv) into a string grid that a
// page's parser can validate. SheetJS auto-detects the format from the bytes.
export async function fileToGrid(file: File): Promise<string[][]> {
  const XLSX = await import('xlsx');
  const wb = XLSX.read(await file.arrayBuffer());
  const sheetName = wb.SheetNames[0];
  if (!sheetName) return [];
  const ws = wb.Sheets[sheetName];
  if (!ws) return [];
  // header:1 → array-of-arrays; raw:false → formatted strings; defval keeps gaps.
  const grid = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: false, defval: '' });
  return grid.map((row) => row.map(cellToString));
}

// Parse a spreadsheet truthy string into a boolean (true/1/yes/y/ใช่).
export function parseBool(v: string | undefined, fallback: boolean): boolean {
  const s = (v ?? '').trim().toLowerCase();
  if (s === '') return fallback;
  return s === 'true' || s === '1' || s === 'yes' || s === 'y' || s === 'ใช่';
}

// Small helper for header-row column lookups used by page parsers. Returns a
// `cell(cols, name)` getter that trims, plus the resolved header index map.
export function headerIndexer(header: string[] | undefined) {
  const trimmed = (header ?? []).map((h) => h.trim());
  const idx = (name: string) => trimmed.indexOf(name);
  const cell = (cols: string[], name: string): string => {
    const i = idx(name);
    return i === -1 ? '' : (cols[i] ?? '').trim();
  };
  return { idx, cell, trimmed };
}
